import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../firebase/useAuth';
import { useMetadata } from '../../contexts/MetadataProvider';
import { useModal } from '../../context/ModalContext';
import { useToast } from '../../context/ToastContext';
import { fetchReviewSentencesSessionItems } from '../../firebase/fetchReviewSentencesSessionItems';
import { updateItemReview } from '../../firebase/reviewSession';
import { skipSentence } from '../../firebase/sentences';
import { getSound } from '../../firebase/sounds';
import levelUpSoundFile from '../../assets/sounds/correct.mp3';
import levelDownSoundFile from '../../assets/sounds/wrong.wav';

// Components
import SessionLayout from '../../components/session/SessionLayout';
import InputSection from '../../components/session/InputSection';
import InteractiveCard from '../../components/session/InteractiveCard';
import Spinner from '../../components/common/Spinner';
import DetailsModal from '../../components/modals/DetailsModal/DetailsModal';
import ReportIssueModal from '../../components/modals/ReportIssueModal';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import SimpleAnswerSection from '../../components/session/SimpleAnswerSection';
import { AudioPlayerProvider } from '../../context/AudioPlayerContext';
import { checkSentenceAnswer } from '../../utils/answerCheck';
import { willEarnShieldToday } from '../../utils/streak';

const ReviewSentencesSessionPage = () => {
  const { userId, userData, userLoading, emailVerified, subscriptionStatus } = useAuth();
  const { learnedWordIds, wordIdsLoading, reviewedSentencesData, reviewedWordsData, learnedTodayData, loading: metadataLoading } = useMetadata();
  const { showModal, hideModal, modalStack } = useModal();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [reviewSentences, setReviewSentences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [checkSubmitted, setCheckSubmitted] = useState(false);
  const [inputResults, setInputResults] = useState([]);
  const [lastProcessedIndex, setLastProcessedIndex] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [levelUpAlertState, setLevelUpAlertState] = useState({ show: false, level: '', type: '' });

  const [audioUrl, setAudioUrl] = useState(null);
  const [audioFetchKey, setAudioFetchKey] = useState(0);
  const [sendResult, setSendResult] = useState(null);
  const [ignoreClicked, setIgnoreClicked] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);
  const [wrapUpFlag, setWrapUpFlag] = useState(false);
  const [lastSkipped, setLastSkipped] = useState(false);
  const [seenCount, setSeenCount] = useState(0);
  const [userChoice, setUserChoice] = useState(null);
  const [correctSentences, setCorrectSentences] = useState([]);
  const [wrongSentences, setWrongSentences] = useState([]);
  const [markedWrongIds, setMarkedWrongIds] = useState(new Set());
  const [sessionSettings, setSessionSettings] = useState(() => {
    return (
      JSON.parse(localStorage.getItem('sessionSettings')) || {
        playCorrectSound: true,
        playWrongSound: true,
        showTranslation: true,
        inputMode: 'mixed',
        simpleAnswerMode: false,
        showFlippingAnimation: true,
        reducedAnimations: false,
        showKaraokeMode: true,
          autoPlayAfterAnswer: false,
          stayOnQuestionUntilCorrect: false,
      }
    );
  });
  const [pendingRetry, setPendingRetry] = useState(false);

  // Refs and Memos
  const inputRef = useRef(null);
  const initialFetchDone = useRef(false);
  const shieldEarnedRef = useRef(null); // whether this session will earn a 盾 (decided at start)
  const playCountsRef = useRef({}); // plays accumulated per item-id across attempts; recorded when the item is graded
  const levelUpSound = useMemo(() => new Audio(levelUpSoundFile), []);
  const levelDownSound = useMemo(() => new Audio(levelDownSoundFile), []);
  const currentSentence = reviewSentences[currentSentenceIndex];

  const isFullyLoading = loading || wordIdsLoading;

  useEffect(() => {
    if (userLoading) return; // Wait until auth state is confirmed

    if (userId && !emailVerified) {
      navigate('/dashboard', {
        state: {
          errorMessage: 'Please verify your email to start a session.',
        },
      });
    }
  }, [userLoading, userId, emailVerified, navigate]);

  useEffect(() => {
    if (location.state?.errorMessage) {
      showToast(location.state.errorMessage, 'error');
      // Clear the state to prevent the toast from reappearing on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showToast, navigate, location.pathname]);

  // Focus input after audio plays
  const handlePlayEnd = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleWordClick = useCallback(
    (word) => {
      showModal(<DetailsModal item={{ ...word }} handleClose={hideModal} />);
    },
    [showModal, hideModal]
  );

  const handleReportClick = useCallback(
    (currentUserInput) => {
      showModal(<ReportIssueModal item={currentSentence} onClose={hideModal} userInput={currentUserInput} />);
    },
    [showModal, hideModal, currentSentence]
  );

  const handleCulturalContextClick = useCallback(() => {
    if (currentSentence) {
      showModal(<DetailsModal item={currentSentence} handleClose={hideModal} initialTab="context" />);
    }
  }, [showModal, hideModal, currentSentence]);

  const handlePolitenessClick = useCallback(() => {
    if (currentSentence) {
      showModal(<DetailsModal item={currentSentence} handleClose={hideModal} initialTab="politeness" />);
    }
  }, [showModal, hideModal, currentSentence]);

  const handleSkipConfirm = useCallback(() => {
    if (!currentSentence) return;
    skipSentence(userId, currentSentence.id, subscriptionStatus.isSubscribed)
      .then((result) => { if (result?.status === 'already_skipped') showToast('This sentence is already skipped.', 'info'); })
      .catch(console.error);
    setLastProcessedIndex(currentSentenceIndex);
    // Skipping resolves the item as correct. If it had already been graded
    // wrong, retract that tally too — otherwise the item lands in both counters
    // and the header total overshoots the session size.
    if (sendResult === 'wrong') setWrongCount((p) => p - 1);
    if (sendResult !== 'correct') setCorrectCount((p) => p + 1);
    if (sessionSettings?.playCorrectSound) {
      levelUpSound.play();
    }
    setLevelUpAlertState({ show: true, level: 7, prevLevel: currentSentence.srs_stage, type: 'up' });
    setCheckSubmitted(true);
    setIsCorrect(true);
    setLastSkipped(true);
    setUserChoice(true);
    setSendResult(null);
    setInputResults(
      currentSentence.words.map((w) => ({
        correct: true,
        text: w.sentence_form,
      }))
    );
  }, [currentSentence, userId, subscriptionStatus.isSubscribed, currentSentenceIndex, sendResult, sessionSettings?.playCorrectSound, levelUpSound, showToast]);

  const handleSkipClick = useCallback(() => {
    showModal(
      <ConfirmationModal
        type="skip"
        onClose={hideModal}
        onConfirm={() => {
          handleSkipConfirm();
          hideModal();
        }}
      />
    );
  }, [showModal, hideModal, handleSkipConfirm]);

  const handleCorrectReview = useCallback(() => {
    if (!currentSentence) return;
    setUserChoice(true);
    setLastProcessedIndex(currentSentenceIndex);
    setCorrectCount((prev) => prev + 1);
    setSendResult('correct');
    if (sessionSettings?.playCorrectSound) {
      levelUpSound.play();
    }
    const newLevel = Math.min(currentSentence.srs_stage + 1, 7);
    setLevelUpAlertState({ show: true, level: newLevel, prevLevel: currentSentence.srs_stage, type: 'up' });
  }, [currentSentence, currentSentenceIndex, sessionSettings, levelUpSound]);

  const handleCorrectWrongReview = useCallback(() => {
    if (!currentSentence) return;
    setUserChoice(true);
    setLastProcessedIndex(currentSentenceIndex);
    setWrongCount((prev) => prev + 1);
    setSendResult('wrong');
    if (sessionSettings?.playWrongSound) {
      levelDownSound.play();
    }
    const newLevel = Math.max(currentSentence.srs_stage - 1, 1);
    setLevelUpAlertState({ show: true, level: newLevel, prevLevel: currentSentence.srs_stage, type: 'down' });
  }, [currentSentence, currentSentenceIndex, sessionSettings, levelDownSound]);

  const handleWrongReview = useCallback(() => {
    setUserChoice(false);
    setMarkedWrongIds((prev) => new Set(prev).add(currentSentence.id));
    setPendingRetry(true);
  }, [currentSentence]);

  const handleCheck = useCallback(() => {
    if (!currentSentence || !userInput) return;
    const { inputResults: newInputResults, isCorrect: isInputCorrect } = checkSentenceAnswer(
      currentSentence,
      userInput,
      sessionSettings?.inputMode || 'mixed'
    );

    setInputResults(newInputResults);
    setCheckSubmitted(true);
    setIsCorrect(isInputCorrect);

    if (isInputCorrect && !markedWrongIds.has(currentSentence.id)) {
      handleCorrectReview();
    } else if (isInputCorrect && markedWrongIds.has(currentSentence.id)) {
      handleCorrectWrongReview();
    } else {
      handleWrongReview();
    }
  }, [currentSentence, userInput, sessionSettings, handleCorrectReview, handleCorrectWrongReview, handleWrongReview, markedWrongIds]);

  const navigateToSessionEnd = useCallback(
    (finalCorrect, finalWrong) => {
      const sanitize = (s, isCorrect) => {
        const sanitized = { ...s, learned: true };
        sanitized.srs_stage = isCorrect ? Math.min(7, s.srs_stage + 1) : Math.max(1, s.srs_stage - 1);
        delete sanitized.sentence_ref;
        if (sanitized.words) {
          sanitized.words = sanitized.words.map((w) => {
            const cleanWord = { ...w };
            delete cleanWord.word_id;
            delete cleanWord.word_ref;
            return cleanWord;
          });
        }
        return sanitized;
      };
      const sanitizedCorrect = finalCorrect.map((s) => sanitize(s, true));
      const sanitizedWrong = finalWrong.map((s) => sanitize(s, false));
      navigate('/session/end', {
        state: {
          correctSentences: sanitizedCorrect,
          wrongSentences: sanitizedWrong,
          type: 'sentences',
          shieldEarned: shieldEarnedRef.current,
        },
      });
    },
    [navigate]
  );

  const handleNextSentence = useCallback(() => {
    const shouldStay = pendingRetry && sessionSettings?.stayOnQuestionUntilCorrect;
    setPendingRetry(false);
    if (shouldStay) {
      setCheckSubmitted(false);
      setInputResults([]);
      setIsCorrect(null);
      setUserChoice(null);
      setIgnoreClicked(false);
      setLastProcessedIndex(null);
      setSendResult(null);
      setAudioUrl(null);
      setAudioFetchKey((prev) => prev + 1);
      inputRef.current?.focus();
      return;
    }
    setLevelUpAlertState({ show: false, level: '', type: '' });
    setUserChoice(null);
    setAudioUrl(null); // Reset audio URL immediately

    if (lastProcessedIndex !== null) {
      if (sendResult && currentSentence) {
        const action = sendResult === 'correct' ? 'correct' : 'wrong';
        const playsBeforeSubmit = playCountsRef.current[currentSentence.id] || 0;
        delete playCountsRef.current[currentSentence.id];
        updateItemReview(userId, currentSentence.id, 'sentences', action, subscriptionStatus.isSubscribed, (currentSentence.correct || 0) + (currentSentence.wrong || 0), playsBeforeSubmit, currentSentence.sound_duration).catch((error) => {
          if (error.code === 'already-reviewed') {
            showToast('This item was already reviewed in another session.', 'error');
          } else if (error.code === 'item-is-skipped') {
            showToast('This item has been skipped and cannot be reviewed.', 'error');
          } else if (error.code === 'vacation-mode') {
            showToast('Reviews cannot be submitted while vacation mode is active.', 'error');
          } else if (error.code === 'daily-limit-reached') {
            showToast('Daily review limit reached.', 'error');
          } else {
            console.error(error);
          }
        });
      }

      let newCorrect = correctSentences;
      let newWrong = wrongSentences;
      if (lastSkipped) {
        // Add the sentence with a 'skipped' flag
        newCorrect = [...correctSentences, { ...currentSentence, skipped: true }];
      } else if (sendResult === 'correct') {
        newCorrect = [...correctSentences, currentSentence];
      } else if (sendResult === 'wrong') {
        newWrong = [...wrongSentences, currentSentence];
      }

      setReviewSentences((prev) => {
        const updated = prev.filter((_, index) => index !== lastProcessedIndex);
        let candidates = updated;
        if (wrapUpFlag) {
          candidates = updated.filter((s) => s.seen);
        }
        if (candidates.length === 0) {
          navigateToSessionEnd(newCorrect, newWrong);
          return [];
        }
        const nextIndexInCandidates = Math.floor(Math.random() * candidates.length);
        const originalIndexOfNext = updated.indexOf(candidates[nextIndexInCandidates]);
        setCurrentSentenceIndex(originalIndexOfNext);
        return updated;
      });

      setCorrectSentences(newCorrect);
      setWrongSentences(newWrong);
      setLastProcessedIndex(null);
      setSendResult(null);
      setLastSkipped(false);
    } else {
      setReviewSentences((prev) => prev.map((s, i) => (i === currentSentenceIndex ? { ...s, seen: true } : s)));
      let candidates = wrapUpFlag ? reviewSentences.filter((s) => s.seen) : reviewSentences;
      const nextCandidates = candidates.filter((_, index) => reviewSentences.indexOf(candidates[index]) !== currentSentenceIndex);

      if (nextCandidates.length > 0) {
        const nextIndexInCandidates = Math.floor(Math.random() * nextCandidates.length);
        const originalIndexOfNext = reviewSentences.indexOf(nextCandidates[nextIndexInCandidates]);
        setCurrentSentenceIndex(originalIndexOfNext);
      }
    }

    setUserInput('');
    setInputResults([]);
    setCheckSubmitted(false);
    setIgnoreClicked(false);
    setIsCorrect(null);
    inputRef.current?.focus();
  }, [
    lastProcessedIndex,
    sendResult,
    currentSentence,
    correctSentences,
    wrongSentences,
    lastSkipped,
    userId,
    subscriptionStatus.isSubscribed,
    wrapUpFlag,
    navigateToSessionEnd,
    reviewSentences,
    currentSentenceIndex,
    showToast,
    pendingRetry,
    sessionSettings?.stayOnQuestionUntilCorrect,
  ]);

  const handleIgnoreButton = useCallback(() => {
    if (userChoice === null || ignoreClicked) return;

    if (sendResult === 'correct') setCorrectCount((prev) => prev - 1);
    if (sendResult === 'wrong') setWrongCount((prev) => prev - 1);

    setIgnoreClicked(true);
    setLastProcessedIndex(null);
    setSendResult(null);
    setIsCorrect(null);
    setLevelUpAlertState({ show: false, level: '', type: '' });
    setUserChoice(null);
    // Ignoring discards this attempt — reset the plays so they don't carry over
    // and inflate "plays before answering" when the item is reviewed again.
    delete playCountsRef.current[currentSentence.id];
    setMarkedWrongIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(currentSentence.id);
      return newSet;
    });
  }, [userChoice, ignoreClicked, sendResult, currentSentence]);

  const handleWrapUpSession = () => {
    setWrapUpFlag((prev) => {
      const isActivating = !prev;
      if (isActivating && currentSentence) {
        setReviewSentences((s) => s.map((item, index) => (index === currentSentenceIndex ? { ...item, seen: true } : item)));
      }
      return isActivating;
    });
  };

  const handleSettingsUpdate = (newSettings) => {
    if (newSettings.simpleAnswerMode !== sessionSettings.simpleAnswerMode && userChoice === null && !ignoreClicked) {
      setCheckSubmitted(false);
      setInputResults([]);
      setIsCorrect(null);
    }
    setSessionSettings(newSettings);
  };

  const handleSimpleModeChoice = (isCorrectChoice) => {
    if (userChoice !== null) return;
    setUserChoice(isCorrectChoice);
    setIsCorrect(isCorrectChoice);
    setInputResults(
      currentSentence.words.map((w) => ({
        correct: isCorrectChoice,
        text: w.sentence_form,
      }))
    );
    if (isCorrectChoice) {
      if (currentSentence && markedWrongIds.has(currentSentence.id)) {
        handleCorrectWrongReview();
      } else {
        handleCorrectReview();
      }
    } else {
      handleWrongReview();
    }
    setCheckSubmitted(true);
  };

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      const message = 'You have unsaved changes. Are you sure you want to leave?';
      event.returnValue = message;
      return message;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffect(() => {
    const preventTouchMove = (e) => {
      if (e.touches?.[0]?.touchType === "stylus") return;
      let el = e.target;
      while (el && el !== document.body) {
        const { overflowY, overflowX } = window.getComputedStyle(el);
        if (
          ((overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight) ||
          ((overflowX === "auto" || overflowX === "scroll") && el.scrollWidth > el.clientWidth)
        ) return;
        el = el.parentElement;
      }
      e.preventDefault();
    };
    document.addEventListener('touchmove', preventTouchMove, { passive: false });
    return () => document.removeEventListener('touchmove', preventTouchMove);
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchAudio = async () => {
      if (currentSentence?.sound) {
        try {
          const url = await getSound(currentSentence.sound, 'sentences');
          if (isMounted) setAudioUrl(url);
        } catch (error) {
          console.error('Error fetching audio URL:', error);
        }
      } else {
        setAudioUrl(null);
      }
    };
    fetchAudio();
    return () => { isMounted = false; };
  }, [currentSentence, audioFetchKey]);

  useEffect(() => {
    setSeenCount(reviewSentences.filter((s) => s.seen).length);
  }, [reviewSentences]);

  useEffect(() => {
    // Decide at session start (before this session's activity) whether finishing
    // today will earn a new 盾, so the end screen can just read the result.
    if (shieldEarnedRef.current === null && !metadataLoading) {
      shieldEarnedRef.current = willEarnShieldToday({
        learnedTodayData,
        reviewedWordsData,
        reviewedSentencesData,
        userTimezone: userData?.settings?.timezone || 'UTC',
        vacationStartedAt: userData?.settings?.vacation_started_at,
        vacationPeriods: userData?.vacation_history,
      });
    }

    const fetchSentences = async () => {
      if (!userId || !userData || initialFetchDone.current) return;
      // Claim the fetch before awaiting. This effect re-runs on every metadata
      // snapshot, and a snapshot landing mid-fetch would otherwise slip past
      // the guard and start a second full session load.
      initialFetchDone.current = true;
      setLoading(true);
      try {
        const sentencesData = await fetchReviewSentencesSessionItems(userId, userData, reviewedSentencesData, subscriptionStatus.isSubscribed);
        if (sentencesData.length === 0) {
          navigate('/dashboard', { state: { errorMessage: 'No sentences to review right now.' } });
          return;
        }
        setReviewSentences(sentencesData);
        setTotalCount(sentencesData.length);
      } catch (error) {
        initialFetchDone.current = false; // release the claim so a later snapshot can retry
        console.error('Error fetching review sentences:', error);
        showToast('Failed to load session.', 'error');
      } finally {
        setLoading(false);
      }
    };

    if (!userLoading && userId && userData && !subscriptionStatus.loading) {
      fetchSentences();
    }
  }, [userId, userData, reviewedSentencesData, reviewedWordsData, learnedTodayData, metadataLoading, navigate, userLoading, showToast, subscriptionStatus.loading, subscriptionStatus.isSubscribed]);

  useEffect(() => {
    if (!isFullyLoading && currentSentence && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isFullyLoading, currentSentence, checkSubmitted]);

  const isAnyModalOpen = modalStack.length > 0;
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key.toLowerCase() === 'i' && checkSubmitted && !isAnyModalOpen && handleIgnoreButton) {
        handleIgnoreButton();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [checkSubmitted, handleIgnoreButton, isAnyModalOpen]);


  const [isPenMode, setIsPenMode] = useState(false);
  useEffect(() => {
    const handlePointerDown = (e) => {
      if (e.pointerType === 'pen') {
        setIsPenMode(true);
      }
    };
    window.addEventListener('pointerdown', handlePointerDown);
    return () => window.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  if ((isFullyLoading && reviewSentences.length === 0) || !currentSentence)
    return (
      <div className="flex items-center justify-center min-h-dvh">
        <Spinner />
      </div>
    );

  return (
    <div className="h-dvh overflow-hidden">
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 z-40">
          <SessionLayout
            updateSettings={handleSettingsUpdate}
            correctCount={correctCount}
            incorrectCount={wrongCount}
            totalCount={totalCount}
            onWrapUp={handleWrapUpSession}
            wrapUpFlag={wrapUpFlag}
            seenCount={seenCount}
          />
        </div>

        {/* Card area */}
        <div className={`${!sessionSettings.simpleAnswerMode && isPenMode ? 'flex-shrink flex-grow-0' : 'flex-1'} min-h-0 flex flex-col w-full max-w-4xl mx-auto`}>
          <AudioPlayerProvider
            audioSrc={audioUrl}
            onPlayEnd={handlePlayEnd}
            onPlay={() => { if (!checkSubmitted && currentSentence) playCountsRef.current[currentSentence.id] = (playCountsRef.current[currentSentence.id] || 0) + 1; }}
            isAutoplay={!checkSubmitted}
            keepAwake={sessionSettings.keepAudioDeviceAwake}
          >
            <InteractiveCard
              currentItem={currentSentence}
              isSubmitted={checkSubmitted}
              showFlippingAnimation={sessionSettings.showFlippingAnimation}
              isIgnored={ignoreClicked}
              isGraded={userChoice !== null}
              showTranslation={checkSubmitted && sessionSettings.showTranslation}
              answerResult={sendResult}
              onItemClick={handleWordClick}
              isHotkeysEnabled={!isAnyModalOpen}
              onIgnore={handleIgnoreButton}
              onSkip={handleSkipClick}
              onReport={handleReportClick}
              onCulturalContext={handleCulturalContextClick}
              onPoliteness={handlePolitenessClick}
              levelUpAlertState={levelUpAlertState}
              learnedWordIds={learnedWordIds}
              userInput={userInput}
              sessionSettings={sessionSettings}
              shrinkToContent={!sessionSettings.simpleAnswerMode && isPenMode}
            />
          </AudioPlayerProvider>
        </div>

        {/* Input section */}
        <div className="flex-shrink-0 w-full bg-background-secondary border-t border-border z-50">
          <div className="w-full max-w-4xl mx-auto p-4 session-input-area">
            {sessionSettings.simpleAnswerMode ? (
              <SimpleAnswerSection
                showResultCard={checkSubmitted}
                onReveal={() => {
                  setCheckSubmitted(true);
                }}
                onChoice={handleSimpleModeChoice}
                userChoice={userChoice}
                isIgnored={ignoreClicked}
                onNext={handleNextSentence}
              />
            ) : (
              <InputSection
                ref={inputRef}
                checkSubmitted={checkSubmitted}
                userInput={userInput}
                inputResults={inputResults}
                setUserInput={setUserInput}
                handleCheck={handleCheck}
                handleNext={handleNextSentence}
                isCorrect={isCorrect}
                isIgnored={ignoreClicked}
                isAnyModalOpen={isAnyModalOpen}
                inputMode={sessionSettings?.inputMode || 'mixed'}
                autoConvertToKana={sessionSettings?.autoConvertToKana ?? true}
                reducedAnimations={sessionSettings?.reducedAnimations}
              />
            )}
          </div>
        </div>

        {/* Palm rest spacer: only in pen mode */}
        {!sessionSettings.simpleAnswerMode && isPenMode && <div className="flex-1 min-h-0 pointer-events-none" />}
      </div>
    </div>
  );
};

export default ReviewSentencesSessionPage;
